import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DownloadIcon from "@mui/icons-material/Download";
import axiosInstance from "@API/axios";
import { EXIT_REQUEST_EXPORT_ATTRIBUTES } from "@API/APIRoutes";
import themeCreator from "@Themes/themeCreator";

/**
 * The catalogue is identical for every listing and never changes within a session, so it is
 * fetched once and shared rather than re-requested each time a dialog opens.
 */
let cataloguePromise = null;

const loadCatalogue = () => {
  if (!cataloguePromise) {
    cataloguePromise = axiosInstance
      .get(EXIT_REQUEST_EXPORT_ATTRIBUTES)
      .then((response) => response.data || [])
      .catch((error) => {
        // Do not cache a failure — the next open should retry.
        cataloguePromise = null;
        throw error;
      });
  }
  return cataloguePromise;
};

/** Test seam: clears the shared catalogue so each case starts from a known state. */
export const resetExportCatalogueCache = () => {
  cataloguePromise = null;
};

const templateKeysOf = (catalogue) =>
  catalogue.filter((attribute) => attribute.defaultSelected).map((attribute) => attribute.key);

/**
 * Lets the user pick which attributes an export contains. Defaults to the nine columns of
 * template-exitrequest.xlsx; the choice is remembered per listing page.
 */
function ExportColumnsDialog({ open, onClose, onExport, exporting, storageKey }) {
  const theme = themeCreator();

  const [catalogue, setCatalogue] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setLoading(true);
    setLoadFailed(false);
    loadCatalogue()
      .then((attributes) => {
        setCatalogue(attributes);
        setSelected(restoreSelection(attributes, storageKey));
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, [open, storageKey]);

  const restoreSelection = (attributes, key) => {
    const templateKeys = templateKeysOf(attributes);
    if (!key) {
      return templateKeys;
    }
    try {
      const saved = JSON.parse(sessionStorage.getItem(key));
      if (Array.isArray(saved) && saved.length > 0) {
        // Drop anything the backend no longer offers, so a stale cache can't request a
        // column that would now be rejected.
        const valid = saved.filter((savedKey) =>
          attributes.some((attribute) => attribute.key === savedKey),
        );
        if (valid.length > 0) {
          return valid;
        }
      }
    } catch (e) {
      // Unreadable cache is not worth surfacing; fall through to the template default.
    }
    return templateKeys;
  };

  const persist = (keys) => {
    setSelected(keys);
    if (!storageKey) {
      return;
    }
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(keys));
    } catch (e) {
      // Storage may be unavailable (private browsing); the selection still applies this time.
    }
  };

  const toggle = (key) =>
    persist(
      selected.includes(key)
        ? selected.filter((selectedKey) => selectedKey !== key)
        : [...selected, key],
    );

  const handleExport = () => {
    // Emit in catalogue order so the sheet reads the same regardless of tick order.
    const ordered = catalogue
      .filter((attribute) => selected.includes(attribute.key))
      .map((attribute) => attribute.key);
    onExport(ordered);
  };

  return (
    <Dialog open={open} onClose={exporting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          py: 2,
          px: 3,
        }}
      >
        {/* DialogTitle already renders an h2, so this must not introduce a nested heading. */}
        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
          Export to Excel
        </Typography>
        <IconButton
          aria-label="Close export dialog"
          onClick={onClose}
          disabled={exporting}
          sx={{ color: theme.palette.common.white }}
          size="small"
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {loadFailed && (
          <Typography color="error" sx={{ py: 2 }}>
            Could not load the list of columns. Close this dialog and try again.
          </Typography>
        )}

        {!loading && !loadFailed && (
          <>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              The export includes every request matching the filters currently applied to this
              list.
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <Button size="small" onClick={() => persist(templateKeysOf(catalogue))}>
                Template
              </Button>
              <Button
                size="small"
                onClick={() => persist(catalogue.map((attribute) => attribute.key))}
              >
                All
              </Button>
              <Button size="small" onClick={() => persist([])}>
                None
              </Button>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {catalogue.map((attribute) => (
                <FormControlLabel
                  key={attribute.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={selected.includes(attribute.key)}
                      onChange={() => toggle(attribute.key)}
                    />
                  }
                  label={attribute.header}
                />
              ))}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          color="primary"
          startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          disabled={exporting || loading || loadFailed || selected.length === 0}
        >
          {exporting ? "Exporting..." : "Export"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ExportColumnsDialog;
